import { apiClient, apiRequest } from '../lib/apiClient';

// Mirrors backend/src/routes/care-team-workspace.routes.ts +
// the /care-team-auth/clients endpoint.

export type ClientRow = {
  connectionId: string;
  householdId: string;
  youthSnapshot: { name?: string; dob?: string } | null;
  connectedAt: string | null;
};

export type EventStatus = 'pending' | 'confirmed' | 'declined' | 'cancelled';

export type CareEvent = {
  id: string;
  connectionId: string;
  title: string;
  date: string;
  time: string;
  location: string;
  notes: string;
  status: EventStatus;
  confirmedAt: string | null;
  attachmentUrl: string;
  attachmentName: string;
  hasAttachmentFile: boolean;
  isFree: boolean;
  cost: number;
  currency: string;
  formUrl: string;
  createdAt: string;
};

export type Message = {
  id: string;
  senderId: string;
  senderRole: 'care_team' | 'caregiver';
  text: string;
  readAt: string | null;
  recipientRole: 'caregiver' | 'youth' | 'both';
  createdAt: string;
};

export type FundingRecord = {
  id: string;
  connectionId: string;
  funder: string;
  amount: number;
  currency: string;
  period: 'monthly' | 'annually' | 'one_time';
  serviceType: string;
  notes: string;
  startDate: string | null;
  endDate: string | null;
  createdAt: string;
};

export type MedicationRecord = {
  id: string;
  name: string;
  dosage: string;
  frequency: string;
  times: string[];
  administeredBy: string;
  notes: string;
  startDate: string;
  endDate: string | null;
};

export type AttachmentPayload = {
  fileData: string;
  fileName?: string;
  mimeType?: string;
};

export type LineItem = { description: string; quantity: number; unitPrice: number };

export type InvoiceStatus = 'draft' | 'sent' | 'paid' | 'cancelled';

export type Invoice = {
  id: string;
  connectionId: string;
  invoiceNumber: string;
  lineItems: LineItem[];
  totalAmount: number;
  currency: string;
  status: InvoiceStatus;
  dueDate: string | null;
  notes: string;
  paidAt: string | null;
  hasAttachmentFile: boolean;
  createdAt: string;
};

export type NewInvoice = {
  lineItems: LineItem[];
  currency?: string;
  dueDate?: string;
  notes?: string;
};

export type MileageEntry = {
  id: string;
  connectionId: string | null;
  date: string;
  startLocation: string;
  endLocation: string;
  distance: number;
  purpose: string;
  ratePerKm: number;
  totalAmount: number;
  createdAt: string;
};

export type NewMileage = {
  date: string;
  startLocation?: string;
  endLocation?: string;
  distance: number;
  purpose?: string;
  ratePerKm?: number;
  connectionId?: string;
};

export type BroadcastEvent = {
  connectionIds: string[];
  title: string;
  date: string;
  time?: string;
  location?: string;
  notes?: string;
  attachmentUrl?: string;
  attachmentName?: string;
  isFree?: boolean;
  cost?: number;
  currency?: string;
  formUrl?: string;
};

export const workspaceApi = {
  clients: () => apiClient.get<ClientRow[]>('/care-team-auth/clients'),

  events: (connectionId: string) =>
    apiClient.get<CareEvent[]>(`/care-team-workspace/${connectionId}/events`),

  eventAttachment: (eventId: string) =>
    apiClient.get<AttachmentPayload>(`/care-team-workspace/events/${eventId}/attachment`),

  messages: (connectionId: string) =>
    apiClient.get<Message[]>(`/care-team-workspace/${connectionId}/messages`),

  sendMessage: (
    connectionId: string,
    text: string,
    recipientRole: Message['recipientRole'] = 'both',
  ) =>
    apiClient.post<Message>(`/care-team-workspace/${connectionId}/messages`, {
      text,
      recipientRole,
    }),

  services: (connectionId: string) =>
    apiClient
      .get<{ services: string[] }>(`/care-team-workspace/${connectionId}/services`)
      .then((r) => r.services),

  funding: (connectionId: string) =>
    apiClient.get<FundingRecord[]>(`/care-team-workspace/${connectionId}/funding`),

  medications: (connectionId: string) =>
    apiClient.get<MedicationRecord[]>(`/care-team-workspace/${connectionId}/medications`),

  // ── Invoices ──────────────────────────────────────────────────────────────
  invoices: (connectionId: string) =>
    apiClient.get<Invoice[]>(`/care-team-workspace/${connectionId}/invoices`),

  createInvoice: (connectionId: string, body: NewInvoice) =>
    apiClient.post<Invoice>(`/care-team-workspace/${connectionId}/invoices`, body),

  setInvoiceStatus: (connectionId: string, invoiceId: string, status: InvoiceStatus) =>
    apiClient.patch<Invoice>(
      `/care-team-workspace/${connectionId}/invoices/${invoiceId}/status`,
      { status },
    ),

  // ── Mileage ───────────────────────────────────────────────────────────────
  mileage: () => apiClient.get<MileageEntry[]>('/care-team-workspace/mileage'),

  createMileage: (body: NewMileage) =>
    apiClient.post<MileageEntry>('/care-team-workspace/mileage', body),

  deleteMileage: (id: string) =>
    apiClient.delete<{ ok: true }>(`/care-team-workspace/mileage/${id}`),

  // ── Broadcast ─────────────────────────────────────────────────────────────
  broadcastMessage: (connectionIds: string[], text: string) =>
    apiClient.post<{ sent: number }>('/care-team-workspace/broadcast/message', {
      connectionIds,
      text,
    }),

  broadcastEvent: (body: BroadcastEvent) =>
    apiClient.post<{ created: CareEvent[]; count: number }>(
      '/care-team-workspace/broadcast/event',
      body,
    ),
};

export { apiRequest };
